import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { adminSettingCompanySchemas } from "../../../../schemas/adminSettingPage";
import {
  checkDomain,
  updateTenantInfo,
} from "../../../../services/apiSystemSettings";
import useMutate from "../../../../hooks/useMutate";
import {
  buildCompanyInfoPayload,
  validateTenantDomain,
} from "../utils/systemSettingUtils";
import { useSettingsDirtyState } from "./useSettingsDirtyState";

export function useCompanyInfoForm(initialData) {
  const methods = useForm({
    resolver: yupResolver(adminSettingCompanySchemas),
    defaultValues: initialData,
  });
  const { mutate, isPending } = useMutate(updateTenantInfo, ["tenant-details"]);
  const domainUrl = useMemo(() => window.location.hostname, []);
  const { hasChanges } = useSettingsDirtyState(methods, initialData);

  const validateDomain = useCallback(
    async (domain) => {
      const localValidationError = validateTenantDomain(domain, initialData?.domain);

      if (localValidationError) {
        return localValidationError;
      }

      if (domain && domain !== initialData?.domain) {
        try {
          const res = await checkDomain({ domain });
          const { duplicate_domain } = res.data.data.responseData;

          if (duplicate_domain) {
            return "The domain you selected is already taken.";
          }
        } catch (_err) {
          return "An error occurred while checking the domain.";
        }
      }

      return null;
    },
    [initialData]
  );

  const onSubmit = useCallback(
    async (formData) => {
      const domainError = await validateDomain(formData.domain);

      if (domainError) {
        methods.setError("domain", {
          type: "manual",
          message: domainError,
        });
        return;
      }

      mutate(buildCompanyInfoPayload(formData, initialData.domain));
    },
    [initialData.domain, methods, mutate, validateDomain]
  );

  return {
    methods,
    isPending,
    domainUrl,
    hasChanges,
    onSubmit,
    validateDomain,
  };
}
